import React, { memo, useMemo } from 'react';
import { demeterPositionsApi } from '../../../../../Apis/Apis';
import { DemeterCommodity, RunCalculationEngineResponse } from '../../../../../Generated/Raven-Demeter';
import useApi from '../../../../Apis/Hooks/useApiHook';
import { MarginCompositeModel, MarginQuarterValue } from '../../RiskDefinitions';
import DairyRiskProtectionHedgeMapChart from './DairyRiskProtectionHedgeMapChart';
import LivestockRiskProtectionHedgeMapCarousel from './LivestockRiskProtectionHedgeMapCarousel';

interface IMarginPositionsHedgeMapChartsProps {
    runCalculationEngineResponse: RunCalculationEngineResponse;
    getFarmerMarginCalculatorCompositeRowsModels: MarginCompositeModel[];
    isLoading: boolean;
    selectedCalculatorCommodity: DemeterCommodity;
    displayDecimalPlaces: number;
    endingQuarter?: MarginQuarterValue;
    startingQuarter?: MarginQuarterValue;
}

const isDairyRevenueProtectionCommodity = (commodity: DemeterCommodity): boolean =>
    commodity === DemeterCommodity.Milk || commodity === DemeterCommodity.MilkClass3 || commodity === DemeterCommodity.MilkClass4;

const MarginPositionsHedgeMapCharts: React.FC<IMarginPositionsHedgeMapChartsProps> = (props: IMarginPositionsHedgeMapChartsProps) => {
    const [isListInsurancePositionsByCommodityLoading, , listInsurancePositionsByCommodityResponse] = useApi(() =>
        demeterPositionsApi.listInsurancePositionsByCommodity(props.selectedCalculatorCommodity, undefined, undefined, -1, 0),
    );

    const stateLabel = useMemo(() => {
        if (!props.runCalculationEngineResponse?.results?.[0]?.lines || !props.runCalculationEngineResponse?.optionsMap?.state) {
            return null;
        }

        const stateValue = props.runCalculationEngineResponse.results[0].lines.find((line) => line.variableName === 'state')?.value;
        if (!stateValue) {
            return null;
        }

        const stateOption = props.runCalculationEngineResponse.optionsMap.state.find((option) => option.value === stateValue);
        return stateOption?.label ?? null;
    }, [props.runCalculationEngineResponse]);

    const filteredPositions = useMemo(() => {
        if (!listInsurancePositionsByCommodityResponse?.rows || !stateLabel) {
            return listInsurancePositionsByCommodityResponse?.rows || [];
        }

        return listInsurancePositionsByCommodityResponse.rows.filter((position) => position.state === stateLabel);
    }, [listInsurancePositionsByCommodityResponse, stateLabel]);

    if (!filteredPositions || filteredPositions.length === 0) {
        if (!isListInsurancePositionsByCommodityLoading) {
            return null;
        }
    }

    if (isDairyRevenueProtectionCommodity(props.selectedCalculatorCommodity)) {
        return (
            <DairyRiskProtectionHedgeMapChart
                runCalculationEngineResponse={props.runCalculationEngineResponse}
                getFarmerMarginCalculatorCompositeRowsModels={props.getFarmerMarginCalculatorCompositeRowsModels}
                filteredPositions={filteredPositions}
                isLoading={isListInsurancePositionsByCommodityLoading || props.isLoading}
                displayDecimalPlaces={props.displayDecimalPlaces}
                endingQuarter={props.endingQuarter}
                startingQuarter={props.startingQuarter}
            />
        );
    }

    return (
        <LivestockRiskProtectionHedgeMapCarousel
            runCalculationEngineResponse={props.runCalculationEngineResponse}
            getFarmerMarginCalculatorCompositeRowsModels={props.getFarmerMarginCalculatorCompositeRowsModels}
            filteredPositions={filteredPositions}
            isLoading={isListInsurancePositionsByCommodityLoading || props.isLoading}
            displayDecimalPlaces={props.displayDecimalPlaces}
            startingQuarter={props.startingQuarter}
            endingQuarter={props.endingQuarter}
        />
    );
};

export default memo(MarginPositionsHedgeMapCharts);
